import { FunctionComponent, useEffect, useRef, useState } from "react";

import { useLoaderData } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
  FirestoreGameData,
  findLobby,
  getCurrentUser,
  hasJoinedLobby,
  joinLobbyAsPlayerTwo,
  closePublicLobbyByCode,
  writePlayerDisconnect,
  clearPlayerDisconnect,
} from "./Firebase";
// signIn убран — авторизация через onAuthStateChanged в App.tsx
import { onSnapshot } from "firebase/firestore";
import DisconnectOverlay from "./DisconnectOverlay";
import { GameState, setState } from "./store/gameStateSlice";
import { setPlayersState } from "./store/playersSlice";
import {
  Actions,
  ActionsContext,
  NetworkedGameActions,
} from "./ActionsContext";
import { setDiceState } from "./store/diceSlice";
import { setCurrentPlayer } from "./store/currentPlayerSlice";
import {
  enqueueNetworkedMoves,
  invalidateNetworkedMoves,
} from "./store/animatableMovesSlice";
import { getClientPlayer, isGameOverState } from "./Utils";
import GameRoom from "./GameRoom";
import { setDoublingCubeData } from "./store/doublingCubeSlice";
import { setMatchScore } from "./store/matchScoreSlice";
import { setGameBoardState, areBoardStatesEquivalent } from "./store/gameBoardSlice";
import { store } from "./store/store";
import { GameMode, Player } from "./Types";
import {
  default as MatchSettingsMenu,
  MatchPointValue,
} from "./MatchSettingsMenu";
import { setGameMode, setShowGameOverDialog } from "./store/settingsSlice";
import { setReadyForNextGameData } from "./store/readyForNextGameSlice";
import { setBetAmount } from "./store/betSlice";
import RoomConnectionError, {
  RoomConnectionErrorType,
} from "./RoomConnectionError";

type LoaderData = {
  roomCode: string;
};

export function loader({ params }: any): LoaderData {
  return { roomCode: params.roomCode };
}

const NetworkedGameRoom: FunctionComponent = () => {
  const { roomCode } = useLoaderData() as LoaderData;
  const [disconnectedData, setDisconnectedData] = useState<Record<string,any> | null>(null);
  const lobbyDocRef = useRef<any>(null);
  const mySlotRef   = useRef<"playerOne"|"playerTwo"|null>(null);
  const [playerJoinedToast, setPlayerJoinedToast] = useState<string | null>(null);
  const prevPlayerTwoRef = useRef<string | null>(null);

  const [settings, gameState, userProfile, animatableMovesLen, provisionalMovesLen, localGameBoard] = useAppSelector((state) => [
    state.settings,
    state.gameState,
    state.user.profile,
    state.animatableMoves.length,
    state.provisionalMoves.length,
    state.gameBoard,
  ]);
  const dispatch = useAppDispatch();
  const lastServerBoardRef = useRef<any>(null);
  const lastProcessedMovesIdRef = useRef<number | null>(null);

  const [matchPointsValue, setMatchPointsValue] = useState<MatchPointValue>(1);
  const [enableDoubling, setEnableDoubling] = useState(false);
  const [roomConnectionError, setRoomConnectionError] = useState(
    RoomConnectionErrorType.None
  );
  const [lobbyDocId, setLobbyDocId] = useState<string | undefined>(undefined);

  const [_gameActions, _setGameActions] = useState<Actions | null>(null);
  const gameActionsRef = useRef(_gameActions);
  function setGameActions(gameActions: Actions) {
    gameActionsRef.current = gameActions;
    _setGameActions(gameActions);
  }

  const [_previousGameState, _setPreviousGameState] =
    useState<GameState>(gameState);
  const previousGameStateRef = useRef(_previousGameState);
  function setPreviousGameState(gs: GameState) {
    previousGameStateRef.current = gs;
    _setPreviousGameState(gs);
  }

  const userProfileRef = useRef(userProfile);
  useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);

  // Автосинк: когда все анимации отыграли и провизорных ходов нет,
  // а серверный gameBoard известен и отличается от локального — синкаем.
  // Это страхует от любых рассинхронов (пропущенных снэпшотов, багов анимаций).
  useEffect(() => {
    if (animatableMovesLen > 0 || provisionalMovesLen > 0) return;
    const serverBoard = lastServerBoardRef.current;
    if (!serverBoard) return;
    if (areBoardStatesEquivalent(localGameBoard, serverBoard)) return;
    // Задержка чтобы не конфликтовать с финальным dispatch в onAnimationComplete
    const t = setTimeout(() => {
      const srv = lastServerBoardRef.current;
      if (!srv) return;
      if (!areBoardStatesEquivalent(store.getState().gameBoard, srv)) {
        dispatch(setGameBoardState(srv));
      }
    }, 300);
    return () => clearTimeout(t);
  }, [animatableMovesLen, provisionalMovesLen, localGameBoard, dispatch]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    async function connectToLobby() {
      const docRef = await findLobby(roomCode);
      if (docRef == null) {
        setRoomConnectionError(RoomConnectionErrorType.NotFound);
        return;
      }

      lobbyDocRef.current = docRef;
      localStorage.setItem("ng_last_room", roomCode);

      // При входе сбрасываем свой disconnect (вернулись в игру)
      const uid = getCurrentUser()?.uid;
      // Слот определим в первом снэпшоте

      unsubscribe = onSnapshot(docRef, (doc) => {
          let data = doc.data() as FirestoreGameData;

          // Запоминаем последний серверный gameBoard — для автосинка
          // через useEffect после завершения анимаций.
          lastServerBoardRef.current = data.gameBoard;

          // Читаем данные об отключениях
          setDisconnectedData((data as any).disconnected ?? null);


          dispatch(setState(data.gameState));
          dispatch(setPlayersState(data.players));
          // Сигнал хосту когда второй игрок подключился
          if (
            gameActionsRef.current?.isHostClient() &&
            data.players.playerTwo != null &&
            prevPlayerTwoRef.current == null
          ) {
            const nick = (data.players.playerTwo as any)?.nickname ?? "Соперник";
            setPlayerJoinedToast(nick);
            setTimeout(() => setPlayerJoinedToast(null), 3500);
            // Звуковой сигнал (Web Audio API — без файлов)
            try {
              const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain); gain.connect(ctx.destination);
              osc.type = "sine";
              osc.frequency.setValueAtTime(660, ctx.currentTime);
              osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12);
              gain.gain.setValueAtTime(0, ctx.currentTime);
              gain.gain.linearRampToValueAtTime(0.35, ctx.currentTime + 0.02);
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
              osc.start(ctx.currentTime);
              osc.stop(ctx.currentTime + 0.45);
            } catch (_) {}
          }
          prevPlayerTwoRef.current = data.players.playerTwo?.uid ?? null;
          dispatch(setCurrentPlayer(data.currentPlayer));
          dispatch(setDiceState(data.dice));
          dispatch(setDoublingCubeData(data.doublingCube));
          // Синхронизируем режим игры из Firestore
          if (data.gameMode) {
            dispatch(setGameMode(data.gameMode));
          }
          dispatch(setMatchScore(data.matchScore));
          dispatch(setReadyForNextGameData(data.readyForNextGame));
          // Ставка комнаты — сервер = единственный источник истины.
          // Без этого у гостя state.bet был 0 и победитель получал 0₽.
          dispatch(setBetAmount(data.betAmount ?? 0));

          // Проверяем есть ли НОВЫЕ входящие ходы для этого клиента.
          // Старые networkedMoves лежат в снэпшоте всегда (сервер их не чистит),
          // поэтому различаем свежий payload по id.
          const incomingForMe =
            data.networkedMoves != null &&
            getClientPlayer(data.players) === data.networkedMoves.animateFor;
          const isNewIncoming =
            incomingForMe &&
            data.networkedMoves!.id !== lastProcessedMovesIdRef.current;

          if (isNewIncoming) {
            lastProcessedMovesIdRef.current = data.networkedMoves!.id;
            if (gameActionsRef.current == null) {
              dispatch(invalidateNetworkedMoves(data.networkedMoves!));
            } else {
              dispatch(enqueueNetworkedMoves(data.networkedMoves!));
            }
          }

          // Принцип: сервер = единственный источник истины для gameBoard.
          // Синхронизируем ТОЛЬКО если:
          //   - первое подключение, ИЛИ
          //   - нет входящих ходов в этом снэпшоте (иначе анимация их применит),
          //     И очередь анимаций пуста, И нет провизорных ходов игрока,
          //     И локальный борд не совпадает с серверным.
          const isFirstConnection = gameActionsRef.current == null;
          const reduxState = store.getState();
          const hasPendingAnimations = reduxState.animatableMoves.length > 0;
          const hasProvisionalMoves = reduxState.provisionalMoves.length > 0;
          const isSafeToSync =
            !isNewIncoming &&
            !hasPendingAnimations &&
            !hasProvisionalMoves;

          if (isFirstConnection || isSafeToSync) {
            if (!areBoardStatesEquivalent(reduxState.gameBoard, data.gameBoard)) {
              dispatch(setGameBoardState(data.gameBoard));
            }
          }
          if (
            previousGameStateRef.current !== GameState.GameOverForfeit &&
            data.gameState === GameState.GameOverForfeit
          ) {
            dispatch(setShowGameOverDialog(true));
          }
          setPreviousGameState(data.gameState);

          let isHost = true;
          if (!hasJoinedLobby(data.players)) {
            if (data.players.playerTwo != null) {
              setRoomConnectionError(RoomConnectionErrorType.TooManyPlayers);
              return;
            } else {
              // Присоединяемся с никнеймом и рейтингом (читаем из ref, не из deps)
              const profile = userProfileRef.current;
              const playerTwoData = profile
                ? {
                    uid: getCurrentUser().uid,
                    nickname: profile.nickname,
                    rating: profile.rating,
                  }
                : { uid: getCurrentUser().uid };
              joinLobbyAsPlayerTwo(docRef, playerTwoData);
              // Remove from public lobbies listing since room is now full
              closePublicLobbyByCode(roomCode);
            }
            isHost = false;
          } else {
            const uid = getCurrentUser().uid;
            isHost =
              data.players.playerOne != null &&
              data.players.playerOne.uid === uid;
          }

          if (gameActionsRef.current == null) {
            dispatch(setGameBoardState(data.gameBoard));
            setLobbyDocId(docRef.id);
            const lobbyGameMode = (data as any).gameMode ?? GameMode.Backgammon;
            setGameActions(new NetworkedGameActions(isHost, dispatch, docRef, lobbyGameMode));

            // Запоминаем слот и сбрасываем disconnect при возврате
            const slot = isHost ? "playerOne" : "playerTwo";
            mySlotRef.current = slot;
            clearPlayerDisconnect(docRef, slot).catch(() => {});

            if (isGameOverState(data.gameState)) {
              dispatch(setShowGameOverDialog(true));
              localStorage.removeItem("ng_last_room"); // игра завершена
            }
          }
        });
    }
    connectToLobby();
    return () => {
      unsubscribe?.();
      lastServerBoardRef.current = null;
      // Если хост уходит пока никто не подключился — удаляем из публичного листинга
      const gameStateNow = gameActionsRef.current;
      if (gameStateNow?.isHostClient()) {
        closePublicLobbyByCode(roomCode).catch(() => {});
      }
      // Пишем disconnect только если игра была в активном состоянии
      // (не GameOver, не WaitingForPlayers, не WaitingToBegin)
      if (mySlotRef.current && lobbyDocRef.current) {
          // Если actions уже созданы — игра шла, пишем disconnect
        if (gameActionsRef.current != null) {
          writePlayerDisconnect(lobbyDocRef.current, mySlotRef.current).catch(() => {});
        }
      }
    };
  }, [dispatch, roomCode]); // userProfile убран — читается через ref

  if (gameActionsRef.current == null) {
    if (settings.showMatchSetupScreen) {
      return (
        <MatchSettingsMenu
          matchPointsValue={matchPointsValue}
          enableDoubling={enableDoubling}
          onMatchPointsChanged={(newValue) => setMatchPointsValue(newValue)}
          onEnableDoublingChanged={(enabled) => setEnableDoubling(enabled)}
          roomCode={roomCode}
        />
      );
    }
    let spinnerOrError = null;
    if (roomConnectionError !== RoomConnectionErrorType.None) {
      spinnerOrError = (
        <RoomConnectionError type={roomConnectionError} roomCode={roomCode} />
      );
    } else {
      spinnerOrError = <div className="Networked-gameboard-spinner" />;
    }
    return spinnerOrError;
  } else {
    let contents = null;
    if (settings.showMatchSetupScreen) {
      contents = (
        <MatchSettingsMenu
          matchPointsValue={matchPointsValue}
          enableDoubling={enableDoubling}
          onMatchPointsChanged={(newValue) => setMatchPointsValue(newValue)}
          onEnableDoublingChanged={(enabled) => setEnableDoubling(enabled)}
          roomCode={roomCode}
        />
      );
    } else {
      contents = (
        <GameRoom
          playerPerspective={
            gameActionsRef.current.isHostClient() ? Player.One : Player.Two
          }
          lobbyDocId={lobbyDocId}
        />
      );
    }
    return (
      <ActionsContext.Provider value={gameActionsRef.current}>
        {contents}
        <DisconnectOverlay disconnectedData={disconnectedData} />
        {playerJoinedToast && (
          <div className="Player-joined-toast">
            🎮 {playerJoinedToast} подключился!
          </div>
        )}
      </ActionsContext.Provider>
    );
  }
};

export default NetworkedGameRoom;
